import { ChefAuthProvider } from './chat/ChefAuthWrapper';
import { Toaster } from '~/components/ui/Toaster';
import { UserProvider } from '~/components/UserProvider';
import { useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useConvexSessionIdOrNullOrLoading } from '~/lib/stores/sessionId';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Button } from '@ui/Button';
import { Spinner } from '@ui/Spinner';
import { useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';

export function Homepage() {
  return (
    <>
      <ChefAuthProvider redirectIfUnauthenticated={false}>
        <UserProvider>
          <MaintenanceHomepage />
        </UserProvider>
      </ChefAuthProvider>
      <Toaster />
    </>
  );
}

function MaintenanceHomepage() {
  return (
    <div className="flex h-full flex-col items-center overflow-y-auto px-6 py-8">
      <div className="flex w-full max-w-2xl flex-col gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight text-content-primary">
          Convex Chef is being replaced by something new
        </h1>
        <p className="text-content-secondary">
          Convex Chef has had a good run but we&rsquo;re in the process of replacing it with a new incarnation better
          suited to long-term application development on Convex. Your Chef projects will continue running but your Chef
          development flow will be impacted in the meantime. From all of us at Convex, thank you for building with Chef.
          We&rsquo;ve loved seeing what you&rsquo;ve made with it, and we apologize for the inconvenience during this
          transition!
        </p>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-content-secondary">
          <li>
            <a
              href="https://dashboard.convex.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-content-link underline hover:no-underline"
            >
              Manage your Convex team
            </a>
          </li>
          <li>
            <a
              href="https://dashboard.convex.dev/team/settings/billing?source=chef"
              target="_blank"
              rel="noopener noreferrer"
              className="text-content-link underline hover:no-underline"
            >
              Manage your Convex subscription
            </a>
          </li>
        </ul>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-content-primary">
          Continuing to develop on Chef projects
        </h2>
        <p className="text-content-secondary">
          If you&rsquo;re a new user we recommend getting started on Convex directly via a coding agent or tutorial. If
          you have an existing Chef app however and don&rsquo;t want to wait for the Chef successor we recommend
          exporting your project for local development:
        </p>

        <section className="rounded-lg border bg-bolt-elements-background-depth-2 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">Recommended flow</p>
          <h3 className="mb-2 mt-1 font-display text-lg font-semibold text-content-primary">
            Developing on your Chef project via your coding agent
          </h3>
          <p className="text-content-secondary">
            Chef projects are fully fledged Convex projects and can be maintained either by hand or via a coding agent
            such as OpenCode or Claude Code.
          </p>
          <p className="mt-3 text-content-secondary">
            <a
              href="https://docs.convex.dev/chef/migration-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="text-content-link underline hover:no-underline"
            >
              Read the migration guide to learn how to port your app
            </a>
            .
          </p>
        </section>

        <RunLocallyDisclosure />

        <ChatList />
      </div>
    </div>
  );
}

const CHATS_PAGE_SIZE = 5;

function ChatList() {
  const sessionId = useConvexSessionIdOrNullOrLoading();
  const chats = useQuery(api.messages.getAll, sessionId ? { sessionId } : 'skip');
  const [visibleCount, setVisibleCount] = useState(CHATS_PAGE_SIZE);

  const isLoading = sessionId === undefined || (sessionId !== null && chats === undefined);
  const visibleChats = chats?.slice(0, visibleCount) ?? [];
  const hasMore = chats ? chats.length > visibleCount : false;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-2xl font-semibold tracking-tight text-content-primary">
        Your chats and source code
      </h2>
      <p className="text-sm text-content-secondary">
        Open a chat to read its history or download its code for local development.
      </p>

      {isLoading ? (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 rounded-lg border bg-bolt-elements-background-depth-2 p-10">
          <Spinner className="ml-0 size-16 text-content-link" />
          <span className="text-sm text-content-secondary">Loading your chats…</span>
        </div>
      ) : sessionId === null ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border bg-bolt-elements-background-depth-2 p-4">
          <LoginButton />
        </div>
      ) : !chats || chats.length === 0 ? (
        <div className="rounded-lg border bg-bolt-elements-background-depth-2 p-4 text-sm text-content-secondary">
          You don&rsquo;t have any Chef chats.
        </div>
      ) : (
        <>
          <ul className="divide-y overflow-hidden rounded-lg border bg-bolt-elements-background-depth-2">
            {visibleChats.map((chat) => {
              const slug = chat.urlId ?? chat.initialId;
              const description = chat.description ?? 'Untitled project';
              return (
                <li key={chat.initialId}>
                  <a
                    href={`/chat/${slug}`}
                    className="group flex items-center justify-between gap-4 px-4 py-3 text-sm font-medium text-content-link transition-colors hover:bg-bolt-elements-background-depth-3 hover:underline focus-visible:bg-bolt-elements-background-depth-3 focus-visible:outline-none"
                  >
                    <span className="truncate">{description}</span>
                    <ChevronRightIcon className="shrink-0 text-content-tertiary transition-transform group-hover:translate-x-0.5 group-hover:text-content-link" />
                  </a>
                </li>
              );
            })}
          </ul>
          {hasMore && (
            <div className="flex justify-center">
              <Button variant="neutral" onClick={() => setVisibleCount((count) => count + CHATS_PAGE_SIZE)}>
                View more chats
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function LoginButton() {
  const { signIn } = useAuth();
  return (
    <Button
      variant="primary"
      onClick={() => {
        void signIn();
      }}
      icon={<img className="size-4" src="/icons/Convex.svg" alt="" />}
    >
      Log in to see your chats
    </Button>
  );
}

function RunLocallyDisclosure() {
  const [open, setOpen] = useState(false);
  return (
    <section className="overflow-hidden rounded-lg border bg-bolt-elements-background-depth-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-bolt-elements-background-depth-3 focus-visible:bg-bolt-elements-background-depth-3 focus-visible:outline-none"
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">Advanced users</p>
          <h3 className="mt-1 font-display text-lg font-semibold text-content-primary">Running Chef locally</h3>
        </div>
        <ChevronDownIcon
          className={`size-5 shrink-0 text-content-secondary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t px-5 py-4">
          <p className="text-content-secondary">
            The Chef codebase is open source and available to run locally or to fork and modify. Projects created by
            open source Chef will still run on the Convex cloud with the same guarantees as regular Convex projects.
          </p>
          <p className="mt-3 text-content-secondary">
            See the Running Locally section at{' '}
            <a
              href="https://github.com/get-convex/chef#running-locally"
              target="_blank"
              rel="noopener noreferrer"
              className="text-content-link underline hover:no-underline"
            >
              https://github.com/get-convex/chef
            </a>{' '}
            for instructions.
          </p>
        </div>
      )}
    </section>
  );
}
